import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {Line} from '@common/notifications/notification-line';
import {useMarkNotificationsAsRead} from '@common/notifications/requests/use-mark-notifications-as-read';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Button} from '@ui/buttons/button';
import {FileDownloadDoneIcon} from '@ui/icons/material/FileDownloadDone';
import {GroupAddIcon} from '@ui/icons/material/GroupAdd';
import {PeopleIcon} from '@ui/icons/material/People';
import {SvgIconProps} from '@ui/icons/svg-icon';
import {MixedImage} from '@ui/images/mixed-image';
import {FormattedRelativeTime} from '@ui/i18n/formatted-relative-time';
import {useSettings} from '@ui/settings/use-settings';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import clsx from 'clsx';
import React, {JSXElementConstructor, useContext} from 'react';
import {Link} from 'react-router';
import {
  DatabaseNotification,
  DatabaseNotificationAction,
} from './database-notification';

const iconMap = {
  'group-add': GroupAddIcon,
  people: PeopleIcon,
  'export-csv': FileDownloadDoneIcon,
} as Record<string, JSXElementConstructor<SvgIconProps>>;

interface NotificationListProps {
  notifications: DatabaseNotification[];
  className?: string;
}
export function NotificationList({
  notifications,
  className,
}: NotificationListProps) {
  const {notifications: config} = useContext(SiteConfigContext);

  return (
    <div className={className}>
      {notifications.map((notification, index) => {
        const isLast = notifications.length - 1 === index;
        const Renderer =
          config?.renderMap?.[notification.type] || NotificationListItem;
        return (
          <Renderer
            key={notification.id}
            notification={notification}
            isLast={isLast}
          />
        );
      })}
    </div>
  );
}

export interface NotificationListItemProps {
  notification: DatabaseNotification;
  onActionButtonClick?: ButtonActionsProps['onActionClick'];
  lineIconRenderer?: JSXElementConstructor<{icon: string}>;
  isLast: boolean;
}
export function NotificationListItem({
  notification,
  onActionButtonClick,
  lineIconRenderer,
  isLast,
}: NotificationListItemProps) {
  const markAsRead = useMarkNotificationsAsRead();
  const navigate = useNavigate();
  const {base_url} = useSettings();
  const mainAction = notification.data.mainAction;

  const showUnreadIndicator = !notification.data.image && !notification.read_at;

  return (
    <div
      onClick={() => {
        if (!markAsRead.isPending && !notification.read_at) {
          markAsRead.mutate({ids: [notification.id]});
        }
        if (mainAction?.action) {
          // Customization: open the notification link in the SAME window instead
          // of a new tab. If the absolute URL points back into this app, use SPA
          // navigation; otherwise do a same-window full navigation. Relative URLs
          // already navigated in-app. See _changes_3.0.8.php.
          if (isAbsoluteUrl(mainAction.action)) {
            if (base_url && mainAction.action.startsWith(base_url)) {
              navigate(mainAction.action.replace(base_url, '') || '/');
            } else {
              window.location.href = mainAction.action;
            }
          } else {
            navigate(mainAction.action);
          }
        }
      }}
      className={clsx(
        'relative flex items-start gap-14 bg-alt px-32 py-20',
        !isLast && 'border-b',
        mainAction?.action && 'cursor-pointer',
        !notification.read_at
          ? 'bg-elevated hover:bg-primary/10'
          : 'hover:bg-hover',
      )}
      title={mainAction?.label ? mainAction.label : undefined}
    >
      {showUnreadIndicator && (
        <div className="absolute left-16 top-26 h-8 w-8 flex-shrink-0 rounded-full bg-primary shadow" />
      )}
      {notification.data.image && (
        <MixedImage
          className="h-24 w-24 flex-shrink-0 text-muted"
          src={iconMap[notification.data.image] || notification.data.image}
        />
      )}
      <div className="min-w-0">
        {notification.data.lines.map((line, index) => (
          <Line
            iconRenderer={lineIconRenderer}
            notification={notification}
            line={line}
            index={index}
            key={index}
          />
        ))}
        <time className="mt-6 block text-xs text-muted">
          <FormattedRelativeTime date={notification.created_at} />
        </time>
        <ButtonActions
          onActionClick={onActionButtonClick}
          notification={notification}
        />
      </div>
    </div>
  );
}

interface ButtonActionsProps {
  notification: DatabaseNotification;
  onActionClick?: (
    e: React.MouseEvent,
    action: DatabaseNotificationAction,
  ) => void;
}
function ButtonActions({notification, onActionClick}: ButtonActionsProps) {
  const {base_url} = useSettings();
  if (!notification.data.buttonActions) return null;

  // if there's no action handler provided, assume action is internal url and render a link
  return (
    <div className="mt-12 flex items-center gap-12">
      {notification.data.buttonActions.map((action, index) => (
        <Button
          key={index}
          size="xs"
          variant={index === 0 ? 'flat' : 'outline'}
          color={index === 0 ? 'primary' : null}
          elementType={!onActionClick ? Link : undefined}
          to={!onActionClick ? action.action.replace(base_url, '') : undefined}
          onClick={e => {
            onActionClick?.(e, action);
          }}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
}
